<template>
  <section class="admin-page">

    <!-- Header -->
    <header class="admin-header">
      <h1>Audience Metrics</h1>
      <p>Platform-wide audience growth, quality, and verification health.</p>
    </header>

    <!-- FILTERS -->
    <section class="filters">
      <input type="date" v-model="filters.from" />
      <input type="date" v-model="filters.to" />

      <select v-model="filters.source">
        <option value="">All Sources</option>
        <option value="instagram">Instagram</option>
        <option value="youtube">YouTube</option>
        <option value="twitter">X / Twitter</option>
      </select>

      <select v-model="filters.source">
        <option value="">All Influencers</option>
      </select>

      <button class="primary-btn" @click="applyFilters">
        Apply
      </button>
    </section>

    <!-- KPI ROW -->
    <section class="kpi-grid">
      <div class="kpi-card">
        <p>Total Fans</p>
        <h2>{{ stats.total }}</h2>
      </div>

      <div class="kpi-card">
        <p>New Fans</p>
        <h2>{{ stats.new }}</h2>
      </div>

      <div class="kpi-card">
        <p>Verified Fans</p>
        <h2>{{ stats.verified }}</h2>
      </div>

      <div class="kpi-card">
        <p>Verification Rate</p>
        <h2>{{ stats.verificationRate }}%</h2>
      </div>
    </section>

    <!-- CHART ROW -->
    <section class="chart-grid">

      <!-- Growth -->
      <div class="card">
        <h3>Audience Growth</h3>

        <!-- Placeholder chart -->
        <div id="audienceGrowthChart" class="chart-placeholder"></div>
      </div>

      <!-- Verification -->
      <div class="card">
        <h3>Verification Breakdown</h3>

         <div id="verificationDonutChart" class="chart-placeholder"></div>
      </div>

    </section>

    <!-- DROP-OFF + QUALITY -->
    <section class="grid">

      <!-- LEFT -->
      <div class="card">
        <h3>Join Funnel Drop-off</h3>

        <table class="table">
          <thead>
            <tr>
              <th>Step</th>
              <th>Users</th>
              <th>Drop-off</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="f in funnel" :key="f.step">
              <td>{{ f.step }}</td>
              <td>{{ f.count }}</td>
              <td>{{ f.drop }}%</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- RIGHT -->
      <div class="card">
        <h3>Top Audience Sources</h3>

        <ul class="list">
          <li v-for="s in sources" :key="s.name">
            <strong>{{ s.name }}</strong>
            <span class="muted">{{ s.percent }}%</span>
          </li>
        </ul>
      </div>

    </section>

    <!-- RISK SIGNALS -->
    <section class="card">
      <h3>Quality & Risk Signals</h3>

      <div class="info-row" v-for="r in risks" :key="r.label">
        <div>
          <strong>{{ r.label }}</strong>
          <p class="muted">{{ r.desc }}</p>
        </div>
        <span :class="['pill', r.status]">
          {{ r.status }}
        </span>
      </div>
    </section>

  </section>
</template>

<script>
import ApexCharts from "apexcharts";
import { loadAudienceMetricsApi } from "../apis/adminmetricsapis";

export default {
  name: "AdminAudienceMetrics",

  data() {
    return {

      growthChart: null,
      donutChart: null,

      filters: {
        from: "",
        to: "",
        source: ""
      },

      stats: {
        total: 0,
        new: 0,
        verified: 0,
        verificationRate: 0
      },

      funnel: [],

      sources: [],

      risks: []
    };
  },

  async mounted() {
    this.initCharts();
    this.loadMetrics();
  },

  methods: {

    // ✅ INIT CHARTS
    initCharts() {

      // ===== Line Chart =====
      this.growthChart = new ApexCharts(
        document.querySelector("#audienceGrowthChart"),
        {
          chart: {
            type: "line",
            height: 220,
            toolbar: { show: false }
          },

          series: [{
            name: "Fans",
            data: []
          }],

          colors: ["#1e293b"],

          stroke: {
            curve: "smooth",
            width: 3
          },

          xaxis: {
            categories: []
          },

          grid: {
            strokeDashArray: 4
          }
        }
      );

      this.growthChart.render();

      // ===== Donut Chart =====
      this.donutChart = new ApexCharts(
        document.querySelector("#verificationDonutChart"),
        {
          chart: {
            type: "donut",
            height: 220
          },

          series: [0, 0],

          labels: ["Verified", "Pending"],

          colors: [
            "#0f172a",
            "#cbd5f5"
          ],

          legend: {
            position: "bottom"
          }
        }
      );

      this.donutChart.render();
    },

    // ✅ LOAD METRICS
    async loadMetrics() {

      const res = await loadAudienceMetricsApi();

      if (!res.status) return;

      // ================= KPIs =================
      this.stats = res.stats;

      // ================= LINE CHART =================
      if (res.growthChart && this.growthChart) {

        this.growthChart.updateOptions({
          xaxis: {
            categories: res.growthChart.labels
          }
        });

        this.growthChart.updateSeries([{
          name: "Fans",
          data: res.growthChart.data
        }]);
      }

      // ================= DONUT =================
      if (res.verificationBreakdown && this.donutChart) {

        this.donutChart.updateSeries([
          res.verificationBreakdown.verified,
          res.verificationBreakdown.pending
        ]);
      }

      // ================= FUNNEL =================
      if (res.funnel) {

        const f = res.funnel;

        this.funnel = [
          { step: "Invite Link Opened", count: f.invite, drop: 0 },
          { step: "Email Submitted", count: f.email, drop: this.calcDrop(f.invite, f.email) },
          { step: "OTP Verified", count: f.otp, drop: this.calcDrop(f.email, f.otp) },
          { step: "Joined Audience", count: f.joined, drop: this.calcDrop(f.otp, f.joined) }
        ];
      }

      // ================= SOURCES =================
      if (res.sources) {

        const total = this.stats.total || 1;

        this.sources = res.sources.map(s => ({
          name: s.follower_platform,
          percent: ((s.total / total) * 100).toFixed(2)
        }));
      }

      // ================= RISKS =================
      if (res.risks) {

        this.risks = [
          {
            label: "OTP Failure Signals",
            desc: "Detected verification friction",
            status: res.risks.otpFailures > 50 ? "medium" : "low"
          }
        ];
      }
    },

    // ✅ DROP-OFF CALC
    calcDrop(prev, next) {
      if (!prev) return 0;
      return (((prev - next) / prev) * 100).toFixed(2);
    },

    formatNumber(value) {

      const num = Number(value) || 0;

      if (num >= 10000000) return (num / 10000000).toFixed(2) + "Cr";
      if (num >= 100000)   return (num / 100000).toFixed(2) + "L";
      if (num >= 1000)     return (num / 1000).toFixed(2) + "K";

      return num.toFixed(2);
    },

    applyFilters() {
      this.loadMetrics();
    }
  }
};

</script>

<style scoped>
.admin-page {
  max-width: 1440px;
  margin: auto;
  padding: 48px;
}

.admin-header h1 {
  font-size: 36px;
  font-weight: 800;
}

.admin-header p {
  color: #64748b;
  margin-top: 6px;
}

/* Filters */
.filters {
  display: flex;
  gap: 12px;
  margin: 32px 0;
}

.filters input,
.filters select {
  padding: 12px;
  border-radius: 12px;
  background: #f8fafc;
}

/* KPIs */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 32px;
}

.kpi-card {
  background: white;
  padding: 24px;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
}

.kpi-card p {
  font-size: 11px;
  text-transform: uppercase;
  color: #94a3b8;
  font-weight: 700;
}

.kpi-card h2 {
  font-size: 28px;
  font-weight: 800;
}

/* Charts */
.chart-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  margin-bottom: 32px;
}

.chart-placeholder {
  height: 220px;
  background: #f8fafc;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-weight: 700;
}

/* Layout */
.grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  margin-bottom: 32px;
}

/* Card */
.card {
  background: white;
  border-radius: 24px;
  padding: 32px;
  border: 1px solid #e5e7eb;
}

.card h3 {
  font-size: 20px;
  font-weight: 800;
  margin-bottom: 24px;
}

/* Table */
.table {
  width: 100%;
  border-collapse: collapse;
}

.table th,
.table td {
  padding: 14px;
  border-bottom: 1px solid #f1f5f9;
  text-align: left;
}

/* Lists */
.list li {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
}

/* Rows */
.info-row {
  display: flex;
  justify-content: space-between;
  padding: 14px 0;
  border-bottom: 1px solid #f1f5f9;
}

.muted {
  font-size: 12px;
  color: #94a3b8;
}

/* Pills */
.pill {
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 800;
  text-transform: uppercase;
}

.pill.low {
  background: #ecfdf5;
  color: #047857;
}

.pill.medium {
  background: #fffbeb;
  color: #92400e;
}

/* Button */
.primary-btn {
  background: #0f172a;
  color: white;
  padding: 12px 18px;
  border-radius: 14px;
  font-weight: 800;
}

/* Mobile */
@media (max-width: 900px) {
  .kpi-grid,
  .chart-grid,
  .grid {
    grid-template-columns: 1fr;
  }

  .filters {
    flex-direction: column;
  }
}

/* ===============================
   ADMIN AUDIENCE METRICS – MOBILE
   =============================== */
@media (max-width: 640px) {

  /* Page padding */
  .admin-page {
    padding: 20px 16px 72px;
  }

  /* Header */
  .admin-header h1 {
    font-size: 26px;
    line-height: 1.2;
  }

  .admin-header p {
    font-size: 14px;
  }

  /* Filters */
  .filters {
    gap: 10px;
    margin: 20px 0;
  }

  .filters input,
  .filters select {
    padding: 12px;
    font-size: 14px;
    border-radius: 10px;
  }

  .primary-btn {
    width: 100%;
    text-align: center;
  }

  /* KPI cards */
  .kpi-grid {
    gap: 12px;
    margin-bottom: 24px;
  }

  .kpi-card {
    padding: 16px;
    border-radius: 14px;
  }

  .kpi-card h2 {
    font-size: 22px;
  }

  /* Charts */
  .chart-placeholder {
    height: 160px;
    font-size: 13px;
    border-radius: 14px;
  }

  .card {
    padding: 18px;
    border-radius: 18px;
  }

  .card h3 {
    font-size: 18px;
    margin-bottom: 16px;
  }

  /* Tables → horizontal safety */
  .table {
    display: block;
    width: 100%;
    overflow-x: auto;
    white-space: nowrap;
  }

  .table th,
  .table td {
    font-size: 13px;
    padding: 10px 12px;
  }

  /* Lists */
  .list li {
    font-size: 14px;
    padding: 10px 0;
  }

  /* Risk rows */
  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .pill {
    font-size: 10px;
    padding: 4px 8px;
  }
}

</style>
